import { useCallback, useEffect } from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import { useLocalStorageSettings } from '~/domains/common/hooks/useLocalStorageSettings';
import { useTreatments } from '~/domains/common/hooks/useTreatments';

const FLAG = FEATURE.HIDE_MY_CONTESTS_ELIMINATED_TOGGLE;

function useShowEliminatedContests({
  isActiveTab,
  isFinalTab,
  isActionRequired,
  isWebView,
}: {
  isActiveTab: boolean;
  isFinalTab: boolean;
  isActionRequired: boolean;
  isWebView: boolean;
}) {
  const [showEliminated, setShowEliminated, removeShowEliminated] =
    useLocalStorageSettings<boolean>('showEliminatedContests', false);
  const { treatments } = useTreatments([FLAG]);
  const treatment = treatments[FLAG]?.treatment;
  const isStorybookForcingFlagsOn = Boolean(process.env.STORYBOOK);
  const isKillSwitchOn = !isStorybookForcingFlagsOn && treatment?.toLowerCase() === 'on';

  useEffect(() => {
    if (isKillSwitchOn && showEliminated) {
      removeShowEliminated();
    }
  }, [isKillSwitchOn, removeShowEliminated, showEliminated]);

  const onShowEliminatedChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        setShowEliminated(true);
        return;
      }
      removeShowEliminated();
    },
    [removeShowEliminated, setShowEliminated]
  );

  const isEffectivelyOn = showEliminated && !isWebView && !isKillSwitchOn;

  return {
    showEliminated,
    onShowEliminatedChange,
    keepEliminatedActive: isEffectivelyOn && (isActiveTab || isFinalTab) && !isActionRequired,
    isToggleVisible: isActiveTab && !isWebView && !isKillSwitchOn,
    hideEliminated: isActiveTab && !(isEffectivelyOn && !isActionRequired),
  };
}

export default useShowEliminatedContests;
